import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { IconButton, keyframes } from '@mui/material';
import { ChristmasTree } from './ChristmasTree';
import { ChristmasEasterEgg } from './ChristmasEasterEgg';
import { ChristmasLights } from './ChristmasLights';
import { ChristmasColors } from './christmasColors';

/** Clicks must happen within this window to count as a triple click. */
export const TRIPLE_CLICK_WINDOW_MS = 1500;
export const CLICKS_TO_TRIGGER = 3;

const bounce = keyframes`
  0%, 100% { transform: translateY(0) rotate(0deg); }
  30% { transform: translateY(-4px) rotate(-6deg); }
  60% { transform: translateY(0) rotate(6deg); }
`;

const twinkle = keyframes`
  0%, 100% { filter: drop-shadow(0 0 2px ${ChristmasColors.GLOW}); }
  50% { filter: drop-shadow(0 0 10px ${ChristmasColors.GLOW}); }
`;

/**
 * The Christmas tree shown in the header, plus a thin string of fairy lights along the header's top edge.
 * Click the tree three times in quick succession to trigger the easter egg.
 */
export const ChristmasHeaderTree: FC = () => {
  const [eggActive, setEggActive] = useState(false);
  const [bouncing, setBouncing] = useState(false);
  const clickTimes = useRef<number[]>([]);
  const bounceTimeout = useRef<number | undefined>(undefined);

  useEffect(() => () => window.clearTimeout(bounceTimeout.current), []);

  const handleClick = useCallback(() => {
    const now = Date.now();
    clickTimes.current = [...clickTimes.current, now].filter((time) => now - time <= TRIPLE_CLICK_WINDOW_MS);

    // A small hop on every click so the user knows the tree reacts.
    setBouncing(false);
    window.clearTimeout(bounceTimeout.current);
    // Re-trigger the animation on the next frame.
    bounceTimeout.current = window.setTimeout(() => setBouncing(true), 20);

    if (clickTimes.current.length >= CLICKS_TO_TRIGGER) {
      clickTimes.current = [];
      setEggActive(true);
    }
  }, []);

  const handleDone = useCallback(() => setEggActive(false), []);

  let animation = 'none';
  if (eggActive) {
    animation = `${twinkle} 1s ease-in-out infinite`;
  } else if (bouncing) {
    animation = `${bounce} 0.5s ease-out 1`;
  }

  return (
    <>
      <ChristmasLights fast={eggActive} />
      <IconButton
        onClick={handleClick}
        aria-label='Juletre'
        title='God jul!'
        data-testid='ChristmasHeaderTree'
        disableRipple
        sx={{
          ml: 1,
          p: 0.5,
          borderRadius: 2,
          animation,
          '&:hover': { backgroundColor: 'transparent', transform: 'scale(1.1)' },
          transition: 'transform 150ms ease-out',
          '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
        }}>
        <ChristmasTree size={30} lit={eggActive} />
      </IconButton>
      {eggActive && <ChristmasEasterEgg onDone={handleDone} />}
    </>
  );
};

export default ChristmasHeaderTree;
